import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  Paper,
  InputAdornment,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import CatchingPokemonIcon from "@mui/icons-material/CatchingPokemon";
import { useNavigate, Link } from "react-router-dom";
import { GoogleLogin } from "@react-oauth/google";
import axios from "axios";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    const loginInput = email.trim().toLowerCase();
    const trimmedPassword = password.trim();

    if (!loginInput || !trimmedPassword) {
      setError("Bitte alle Felder ausfüllen.");
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post("/api/users/login", {
        login: loginInput,
        password: trimmedPassword,
      });
      localStorage.setItem("token", response.data.token);
      navigate("/profile");
    } catch {
      setError("Login fehlgeschlagen. Benutzername, E-Mail oder Passwort falsch.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setError("");
    setLoading(true);
    try {
      const response = await axios.post("/api/users/google-login", {
        credential: credentialResponse.credential,
      });
      localStorage.setItem("token", response.data.token);
      navigate("/profile");
    } catch {
      setError("Google-Login fehlgeschlagen. Bitte versuche es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100dvh",
        px: 2,
        pb: "80px",
        backgroundColor: "background.default",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <CatchingPokemonIcon sx={{ fontSize: 56, color: "primary.main", mb: 1 }} />
        <Typography variant="h4">PokéGoDex</Typography>
        <Typography variant="body2" color="text.secondary">
          Dein Pokémon GO Begleiter
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 420,
          p: { xs: 2.5, sm: 3.5 },
          border: "1px solid",
          borderColor: "divider",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Google Login */}
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={() => setError("Google-Login fehlgeschlagen.")}
            shape="rectangular"
            size="large"
            text="signin_with"
            locale="de"
            width={360}
          />
        </Box>

        <Divider sx={{ my: 2 }}>
          <Typography variant="caption" color="text.secondary">
            oder mit E-Mail anmelden
          </Typography>
        </Divider>

        <form onSubmit={handleSubmit}>
          <TextField
            label="E-Mail oder Benutzername"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />

          <TextField
            label="Passwort"
            type={showPassword ? "text" : "password"}
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword((p) => !p)}
                    edge="end"
                    aria-label="Passwort anzeigen/verbergen"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={loading}
            sx={{ mt: 2, mb: 1 }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Einloggen"
            )}
          </Button>
        </form>

        <Box sx={{ textAlign: "center", mt: 1.5 }}>
          <Link to="/forgot-password" style={{ textDecoration: "none" }}>
            <Typography variant="body2" color="primary" sx={{ mb: 1 }}>
              Passwort vergessen?
            </Typography>
          </Link>
          <Typography variant="body2" color="text.secondary">
            Noch nicht dabei?{" "}
            <Link to="/registration" style={{ textDecoration: "none" }}>
              <Typography
                component="span"
                variant="body2"
                color="primary"
                fontWeight={600}
              >
                Registrieren
              </Typography>
            </Link>
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default Login;
